import Phaser from "phaser";
import type { PlayerTechState, TechUpgradeId } from "../../../shared/types/BattleEvent";
import towersData from "../game/data/towers.json";
import unitsData from "../game/data/units.json";
import type { Language } from "../i18n";
import { t } from "../i18n";

export type BuildSelection = { kind: "tower" | "unit" | "economy"; id: string; cost: number };

type BuildPanelCallbacks = {
  onSelect: (selection?: BuildSelection) => void;
  onCounterAttack: () => void;
  onUpgradeIncome: () => void;
  onUpgradeCounterCharge: () => void;
  onRepairCastle: () => void;
  onBuildEconomy: () => void;
  onBuyTech: (tech: TechUpgradeId) => void;
};

type BuildButton = {
  selection: BuildSelection;
  background: Phaser.GameObjects.Image;
  icon: Phaser.GameObjects.Image;
  nameText: Phaser.GameObjects.Text;
  costText: Phaser.GameObjects.Text;
};

type UtilityButton = {
  id: "income" | "counter" | "repair" | "vault" | "tech";
  background: Phaser.GameObjects.Image;
  labelText: Phaser.GameObjects.Text;
  costText: Phaser.GameObjects.Text;
  getCost: () => number;
};

type TechButton = {
  id: TechUpgradeId;
  background: Phaser.GameObjects.Image;
  labelText: Phaser.GameObjects.Text;
  costText: Phaser.GameObjects.Text;
};

const shortNames: Record<string, string> = {
  archer_tower: "Archer",
  crossbow_tower: "Crossbow",
  wall_bastion: "Bastion",
  healing_shrine: "Shrine",
  powder_trap: "Powder",
  ice_circle: "Ice Rune",
  watchtower: "Watch",
  heavy_ballista: "Ballista",
  rune_obelisk: "Rune",
  mortar_emplacement: "Mortar",
  command_banner: "Banner",
  swordsman: "Sword",
  shield_soldier: "Shield",
  monk: "Monk",
  powder_carrier: "Barrel",
  frost_mage: "Frost",
  raven_scout: "Scout",
  spearman_new: "Spear",
  alchemist: "Alchemy",
  standard_bearer: "Banner",
  engineer: "Engineer"
};

const shortNamesKo: Record<string, string> = {
  archer_tower: "궁수",
  crossbow_tower: "쇠뇌",
  wall_bastion: "성벽",
  healing_shrine: "성소",
  powder_trap: "화약",
  ice_circle: "빙결",
  watchtower: "감시",
  heavy_ballista: "발리스타",
  rune_obelisk: "룬",
  mortar_emplacement: "박격포",
  command_banner: "깃발",
  swordsman: "검병",
  shield_soldier: "방패",
  monk: "수도",
  powder_carrier: "화약병",
  frost_mage: "냉기",
  raven_scout: "정찰",
  spearman_new: "창병",
  alchemist: "연금",
  standard_bearer: "군기",
  engineer: "공병"
};

export class BuildPanel {
  private readonly goldText: Phaser.GameObjects.Text;
  private readonly counterText: Phaser.GameObjects.Text;
  private readonly queueText: Phaser.GameObjects.Text;
  private readonly upkeepText: Phaser.GameObjects.Text;
  private readonly statusText: Phaser.GameObjects.Text;
  private readonly counterFill: Phaser.GameObjects.Rectangle;
  private readonly buttons: BuildButton[] = [];
  private readonly utilityButtons: UtilityButton[] = [];
  private readonly techButtons: TechButton[] = [];
  private readonly techPanel: Phaser.GameObjects.Container;
  private selectedId = "";
  private currentGold = 0;
  private currentCounterGauge = 0;
  private currentCounterReady = false;
  private currentIncomeUpgradeCost = Infinity;
  private currentCounterUpgradeCost = Infinity;
  private currentCastleRepairCost = Infinity;
  private currentCastleRepairCooldownMs = 0;
  private currentEconomyBuildCost = 180;
  private currentTechState: PlayerTechState = { researched: [], nextCosts: {}, maxReached: false };
  private currentUpkeepPenaltyPercent = 0;
  private currentEffectiveIncomePerSecond = 0;
  private currentEconomyOutposts = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly callbacks: BuildPanelCallbacks,
    private readonly language: Language,
    private readonly uiScale = 1,
    private readonly highContrastText = false
  ) {
    scene.add.image(640, 658, "build_bar").setDepth(130).setScrollFactor(0).setDisplaySize(1248, 116);
    scene.add.image(78, 642, "gold_icon").setDepth(135).setScrollFactor(0).setDisplaySize(25, 25);

    this.goldText = scene.add
      .text(98, 631, "180", {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.fontSize(20),
        color: "#ffe58e",
        stroke: "#111016",
        strokeThickness: this.stroke(3)
      })
      .setResolution(2)
      .setDepth(136)
      .setScrollFactor(0);

    this.statusText = scene.add
      .text(54, 663, "War chest ready", {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.fontSize(13),
        color: "#f6f0df",
        stroke: "#111016",
        strokeThickness: this.stroke(2),
        wordWrap: { width: 132 }
      })
      .setResolution(2)
      .setDepth(136)
      .setScrollFactor(0);
    this.statusText.setText(t(this.language, "warChestReady"));
    this.queueText = scene.add
      .text(54, 698, "", {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.fontSize(12),
        color: "#dff9ff",
        stroke: "#111016",
        strokeThickness: this.stroke(1),
        fixedWidth: 134
      })
      .setResolution(2)
      .setDepth(136)
      .setScrollFactor(0);
    this.upkeepText = scene.add
      .text(54, 614, "", {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.fontSize(11),
        color: "#b9f4c4",
        stroke: "#111016",
        strokeThickness: this.stroke(1),
        fixedWidth: 134
      })
      .setResolution(2)
      .setDepth(136)
      .setScrollFactor(0);

    scene.add.text(210, 616, t(this.language, "towers"), this.labelStyle()).setResolution(2).setDepth(136).setScrollFactor(0);
    scene.add.text(210, 669, t(this.language, "units"), this.labelStyle()).setResolution(2).setDepth(136).setScrollFactor(0);

    this.createButtons();
    this.createUtilityButton("income", 1018, 634, t(this.language, "incomeUpgradeShort"), () => this.currentIncomeUpgradeCost, this.callbacks.onUpgradeIncome);
    this.createUtilityButton("counter", 1018, 688, t(this.language, "counterUpgradeShort"), () => this.currentCounterUpgradeCost, this.callbacks.onUpgradeCounterCharge);
    this.createUtilityButton("repair", 1098, 618, t(this.language, "repairCastleShort"), () => this.currentCastleRepairCost, this.callbacks.onRepairCastle);
    this.createUtilityButton("tech", 1018, 704, t(this.language, "techShort"), () => this.nextCheapestTechCost(), () => this.setTechPanelVisible(!this.techPanel.visible));
    this.createUtilityButton("vault", 1098, 704, t(this.language, "economyBuildShort"), () => this.currentEconomyBuildCost, this.callbacks.onBuildEconomy);
    this.techPanel = this.createTechPanel();
    this.setTechPanelVisible(false);

    scene.add.image(1167, 664, "counter_frame").setDepth(135).setScrollFactor(0).setDisplaySize(150, 40);
    this.counterFill = scene.add.rectangle(1106, 676, 0, 7, 0x5de9ff, 0.95).setOrigin(0, 0.5).setDepth(136).setScrollFactor(0);
    this.counterText = scene.add
      .text(1167, 652, t(this.language, "counter"), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.fontSize(15),
        color: "#e8fbff",
        stroke: "#111016",
        strokeThickness: this.stroke(2)
      })
      .setResolution(2)
      .setOrigin(0.5)
      .setDepth(136)
      .setScrollFactor(0);
    this.createCounterButton();
  }

  update(
    gold: number,
    counterGauge: number,
    counterReady: boolean,
    incomeUpgradeCost = this.currentIncomeUpgradeCost,
    counterUpgradeCost = this.currentCounterUpgradeCost,
    upkeepPenaltyPercent = 0,
    effectiveIncomePerSecond = 0,
    economyOutposts = 0,
    castleRepairCost = this.currentCastleRepairCost,
    castleRepairCooldownMs = this.currentCastleRepairCooldownMs,
    economyBuildCost = this.currentEconomyBuildCost,
    techState = this.currentTechState
  ) {
    this.currentGold = gold;
    this.currentCounterGauge = counterGauge;
    this.currentCounterReady = counterReady;
    this.currentIncomeUpgradeCost = incomeUpgradeCost;
    this.currentCounterUpgradeCost = counterUpgradeCost;
    this.currentCastleRepairCost = castleRepairCost;
    this.currentCastleRepairCooldownMs = castleRepairCooldownMs;
    this.currentEconomyBuildCost = economyBuildCost;
    this.currentTechState = techState;
    this.currentUpkeepPenaltyPercent = upkeepPenaltyPercent;
    this.currentEffectiveIncomePerSecond = effectiveIncomePerSecond;
    this.currentEconomyOutposts = economyOutposts;
    this.goldText.setText(`${Math.floor(gold)}`);
    this.upkeepText.setText(
      upkeepPenaltyPercent > 0
        ? `${t(this.language, "upkeep")} -${Math.round(upkeepPenaltyPercent)}%`
        : effectiveIncomePerSecond > 0
          ? `${t(this.language, "incomeRate")} +${effectiveIncomePerSecond}/s${economyOutposts > 0 ? ` · +${economyOutposts}` : ""}`
          : ""
    );
    this.upkeepText.setColor(upkeepPenaltyPercent > 0 ? "#ffcf9f" : "#b9f4c4");
    this.counterText.setText(counterReady ? t(this.language, "counterReady") : `${t(this.language, "counter")} ${Math.floor(counterGauge)}%`);
    this.counterFill.width = Math.min(132, Math.max(0, counterGauge * 1.32));

    for (const button of this.buttons) {
      const disabled = button.selection.cost > gold;
      button.background.setTexture(this.selectedId === button.selection.id ? "build_card_selected" : disabled ? "build_card_disabled" : "build_card");
      button.icon.setAlpha(disabled ? 0.48 : 1);
      button.nameText.setAlpha(disabled ? 0.56 : 1);
      button.costText.setAlpha(disabled ? 0.56 : 1);
    }

    for (const button of this.utilityButtons) {
      const cost = button.getCost();
      const maxed = !Number.isFinite(cost);
      const coolingDown = button.id === "repair" && this.currentCastleRepairCooldownMs > 0;
      const disabled = maxed || coolingDown || cost > gold;
      button.background.setTexture(disabled ? "build_card_disabled" : "build_card");
      button.labelText.setAlpha(disabled ? 0.56 : 1);
      button.costText.setAlpha(disabled ? 0.56 : 1);
      button.costText.setText(coolingDown ? `${Math.ceil(this.currentCastleRepairCooldownMs / 1000)}s` : maxed ? t(this.language, "maxed") : `${cost}`);
    }
    this.updateTechButtons();
  }

  setStatus(text: string) {
    this.statusText.setText(text);
  }

  setSummonQueue(count: number, nextReadyMs = 0) {
    if (count <= 0) {
      this.queueText.setText("");
      return;
    }
    const seconds = Math.max(0, nextReadyMs / 1000).toFixed(1);
    this.queueText.setText(`${t(this.language, "summonQueueLabel")} ${count}/8 · ${seconds}s`);
  }

  clearSelectedBuild() {
    this.selectedId = "";
    this.update(
      this.currentGold,
      this.currentCounterGauge,
      this.currentCounterReady,
      this.currentIncomeUpgradeCost,
      this.currentCounterUpgradeCost,
      this.currentUpkeepPenaltyPercent,
      this.currentEffectiveIncomePerSecond,
      this.currentEconomyOutposts,
      this.currentCastleRepairCost,
      this.currentCastleRepairCooldownMs,
      this.currentEconomyBuildCost,
      this.currentTechState
    );
  }

  private createButtons() {
    const labels = this.language === "ko" ? shortNamesKo : shortNames;
    const towerEntries = towersData.map((tower) => ({ kind: "tower" as const, id: tower.id, cost: tower.cost, icon: tower.id, label: labels[tower.id] ?? tower.name }));
    const unitEntries = unitsData.map((unit) => ({ kind: "unit" as const, id: unit.id, cost: unit.cost, icon: unit.id, label: labels[unit.id] ?? unit.name }));

    towerEntries.forEach((entry, index) => this.createButton(entry, 246 + index * 76, 634));
    unitEntries.forEach((entry, index) => this.createButton(entry, 246 + index * 76, 688));
  }

  private createButton(entry: BuildSelection & { icon: string; label: string }, x: number, y: number) {
    const container = this.scene.add.container(x, y).setDepth(138).setScrollFactor(0);
    const background = this.scene.add.image(0, 0, "build_card").setDisplaySize(70, 44);
    const icon = this.scene.add.image(-22, -1, entry.icon).setDisplaySize(24, 24);
    const nameText = this.scene.add
      .text(-4, -15, entry.label, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.fontSize(11),
        color: "#f8efc9",
        stroke: "#111016",
        strokeThickness: this.stroke(1),
        fixedWidth: 36
      })
      .setResolution(2)
      .setAlign("left");
    const costText = this.scene.add
      .text(-4, 2, `${entry.cost}`, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.fontSize(12),
        color: "#ffe58e",
        stroke: "#111016",
        strokeThickness: this.stroke(1)
      })
      .setResolution(2)
      .setAlign("left");

    container.add([background, icon, nameText, costText]);
    container.setSize(70, 44);
    container.setInteractive({ useHandCursor: true });
    container.on("pointerdown", () => {
      if (this.selectedId === entry.id) {
        this.selectedId = "";
        this.setStatus(t(this.language, "upgradeModeReady"));
        this.callbacks.onSelect(undefined);
        this.update(
          this.currentGold,
          this.currentCounterGauge,
          this.currentCounterReady,
          this.currentIncomeUpgradeCost,
          this.currentCounterUpgradeCost,
          this.currentUpkeepPenaltyPercent,
          this.currentEffectiveIncomePerSecond,
          this.currentEconomyOutposts,
          this.currentCastleRepairCost,
          this.currentCastleRepairCooldownMs,
          this.currentEconomyBuildCost,
          this.currentTechState
        );
        return;
      }
      if (entry.cost > this.currentGold) {
        this.setStatus(t(this.language, "needMoreGold"));
        return;
      }
      this.selectedId = entry.id;
      this.setStatus(`${entry.label} ${t(this.language, "ready")}`);
      this.callbacks.onSelect({ kind: entry.kind, id: entry.id, cost: entry.cost });
      this.update(
        this.currentGold,
        this.currentCounterGauge,
        this.currentCounterReady,
        this.currentIncomeUpgradeCost,
        this.currentCounterUpgradeCost,
        this.currentUpkeepPenaltyPercent,
        this.currentEffectiveIncomePerSecond,
        this.currentEconomyOutposts,
        this.currentCastleRepairCost,
        this.currentCastleRepairCooldownMs,
        this.currentEconomyBuildCost,
        this.currentTechState
      );
    });
    container.on("pointerover", () => {
      if (entry.cost <= this.currentGold && this.selectedId !== entry.id) background.setTexture("build_card_selected");
    });
    container.on("pointerout", () => {
      background.setTexture(this.selectedId === entry.id ? "build_card_selected" : entry.cost > this.currentGold ? "build_card_disabled" : "build_card");
    });

    this.buttons.push({ selection: { kind: entry.kind, id: entry.id, cost: entry.cost }, background, icon, nameText, costText });
  }

  private createUtilityButton(id: UtilityButton["id"], x: number, y: number, label: string, getCost: () => number, onClick: () => void) {
    const container = this.scene.add.container(x, y).setDepth(139).setScrollFactor(0);
    const background = this.scene.add.image(0, 0, "build_card").setDisplaySize(70, 36);
    const labelText = this.scene.add
      .text(0, -10, label, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.fontSize(11),
        color: "#f8efc9",
        stroke: "#111016",
        strokeThickness: this.stroke(1),
        fixedWidth: 58,
        align: "center"
      })
      .setResolution(2)
      .setOrigin(0.5, 0);
    const costText = this.scene.add
      .text(0, 4, "", {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.fontSize(12),
        color: "#ffe58e",
        stroke: "#111016",
        strokeThickness: this.stroke(1),
        fixedWidth: 58,
        align: "center"
      })
      .setResolution(2)
      .setOrigin(0.5, 0);

    container.add([background, labelText, costText]);
    container.setSize(70, 36);
    container.setInteractive({ useHandCursor: true });
    container.on("pointerdown", onClick);
    container.on("pointerover", () => {
      if (Number.isFinite(getCost()) && getCost() <= this.currentGold && (id !== "repair" || this.currentCastleRepairCooldownMs <= 0)) {
        background.setTexture("build_card_selected");
      }
    });
    container.on("pointerout", () => {
      const cost = getCost();
      const disabled = !Number.isFinite(cost) || cost > this.currentGold || (id === "repair" && this.currentCastleRepairCooldownMs > 0);
      background.setTexture(disabled ? "build_card_disabled" : "build_card");
    });
    this.utilityButtons.push({ id, background, labelText, costText, getCost });
  }

  private createTechPanel() {
    const panel = this.scene.add.container(1012, 496).setDepth(142).setScrollFactor(0);
    const backing = this.scene.add.rectangle(0, 0, 244, 118, 0x071118, 0.88).setStrokeStyle(1, 0x8de7ff, 0.56);
    const title = this.scene.add
      .text(-112, -52, t(this.language, "techPanelTitle"), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.fontSize(13),
        color: "#f8efc9",
        stroke: "#111016",
        strokeThickness: this.stroke(2)
      })
      .setResolution(2);
    panel.add([backing, title]);

    const entries: Array<{ id: TechUpgradeId; x: number; y: number }> = [
      { id: "infantry_drills", x: -60, y: -18 },
      { id: "arcane_focus", x: 60, y: -18 },
      { id: "siege_engineering", x: -60, y: 34 },
      { id: "fortification_plans", x: 60, y: 34 }
    ];
    for (const entry of entries) {
      this.createTechButton(panel, entry.id, entry.x, entry.y);
    }
    return panel;
  }

  private createTechButton(panel: Phaser.GameObjects.Container, id: TechUpgradeId, x: number, y: number) {
    const background = this.scene.add.image(x, y, "build_card").setDisplaySize(108, 42);
    const labelText = this.scene.add
      .text(x, y - 13, this.techLabel(id), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.fontSize(11),
        color: "#f8efc9",
        stroke: "#111016",
        strokeThickness: this.stroke(1),
        fixedWidth: 94,
        align: "center"
      })
      .setResolution(2)
      .setOrigin(0.5, 0);
    const costText = this.scene.add
      .text(x, y + 4, "", {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.fontSize(12),
        color: "#ffe58e",
        stroke: "#111016",
        strokeThickness: this.stroke(1),
        fixedWidth: 94,
        align: "center"
      })
      .setResolution(2)
      .setOrigin(0.5, 0);
    const zone = this.scene.add.zone(x, y, 108, 42).setInteractive({ useHandCursor: true });
    zone.on("pointerdown", () => this.callbacks.onBuyTech(id));
    panel.add([background, labelText, costText, zone]);
    this.techButtons.push({ id, background, labelText, costText });
  }

  private updateTechButtons() {
    for (const button of this.techButtons) {
      const researched = this.currentTechState.researched.includes(button.id);
      const cost = this.currentTechState.nextCosts[button.id] ?? Infinity;
      const disabled = researched || !Number.isFinite(cost) || cost > this.currentGold;
      button.background.setTexture(researched ? "build_card_selected" : disabled ? "build_card_disabled" : "build_card");
      button.labelText.setAlpha(disabled && !researched ? 0.56 : 1);
      button.costText.setAlpha(disabled && !researched ? 0.56 : 1);
      button.costText.setText(researched ? t(this.language, "researched") : Number.isFinite(cost) ? `${cost}` : t(this.language, "maxed"));
    }
  }

  private nextCheapestTechCost() {
    const costs = Object.values(this.currentTechState.nextCosts).filter((cost) => Number.isFinite(cost));
    return costs.length > 0 ? Math.min(...costs) : Infinity;
  }

  private setTechPanelVisible(visible: boolean) {
    this.techPanel.setVisible(visible);
  }

  private techLabel(id: TechUpgradeId) {
    const ko = this.language === "ko";
    if (id === "infantry_drills") return ko ? "보병" : "Infantry";
    if (id === "arcane_focus") return ko ? "마법" : "Arcane";
    if (id === "siege_engineering") return ko ? "공성" : "Siege";
    return ko ? "요새" : "Fortify";
  }

  private createCounterButton() {
    const zone = this.scene.add.zone(1167, 664, 150, 44).setDepth(140).setScrollFactor(0).setInteractive({ useHandCursor: true });
    zone.on("pointerdown", this.callbacks.onCounterAttack);
  }

  private labelStyle(): Phaser.Types.GameObjects.Text.TextStyle {
    return {
      fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
      fontSize: this.fontSize(14),
      color: "#d8c486",
      stroke: "#111016",
      strokeThickness: this.stroke(2)
    };
  }

  private fontSize(base: number) {
    return `${Math.round(base * this.uiScale)}px`;
  }

  private stroke(base: number) {
    return this.highContrastText ? Math.max(2, base + 1) : base;
  }
}
